import {inject, Injectable} from '@angular/core';
import {CmsApiService} from "./cms-api.service";
import {FeatureFlags} from "./definitions/feature-flags";

export type DeepPartial<T> = T extends object ? {
  [P in keyof T]?: DeepPartial<T[P]>;
} : T;


@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private readonly cms: CmsApiService = inject(CmsApiService);

  private featureFlags: FeatureFlags = {
    temporary: {
      clientAccessConfigServer: false,
      metaOperationObjectQueue: false,
      newNameFields: false,
    },
    experimental: {
      useNewFilterMenu: true,
      useNewObjectPage: true,
      useNewMainMenu: true,
    },
    permanent: {
      featureFlags: false
    }
  };

  async initFeatureFlags() {
    return this.cms.getFeatureFlags().then(featureFlags => {
      this.featureFlags = {
        temporary: featureFlags.temporary,
        permanent: featureFlags.permanent,
        experimental: this.featureFlags.experimental
      }

      return this.applyOverrides(featureFlags);
    })
  }

  getFeatureFlags(): FeatureFlags {
    return this.applyOverrides(this.featureFlags);
  }

  getOverrides(): DeepPartial<FeatureFlags> | undefined {
    const overrides = localStorage.getItem('featureFlagsOverrides');
    try {
      //return overrides ? JSON.parse(overrides) : undefined
      return undefined;
    } catch (e) {
      console.error('Error parsing feature flags overrides from localStorage:', e);
      return undefined;
    }
  }

  setOverrides(overrides: DeepPartial<FeatureFlags>) {
    localStorage.setItem('featureFlagsOverrides', JSON.stringify(overrides));
  }

  clearOverrides() {
    localStorage.removeItem('featureFlagsOverrides');
  }

  private applyOverrides(flags: FeatureFlags): FeatureFlags {
    const overrides = this.getOverrides();

    return {
      temporary: {
        ...flags.temporary,
        ...overrides?.temporary
      },
      experimental: {
        ...flags.experimental,
        ...overrides?.experimental
      },
      permanent: {
        ...flags.permanent,
        ...overrides?.permanent
      }
    }
  }

  enableFeatureOverride(feature: string) {
    const overrides: DeepPartial<FeatureFlags> = this.getOverrides() ?? {};
    setValueFromDotNotation(overrides, feature, true);
    this.setOverrides(overrides);
  }

  disableFeatureOverride(feature: string) {
    const overrides: DeepPartial<FeatureFlags> = this.getOverrides() ?? {};
    setValueFromDotNotation(overrides, feature, false);
    this.setOverrides(overrides);
  }

  getFeatureOverride(feature: string) {
    const overrides = this.getOverrides();
    return getValueFromDotNotation(overrides, feature);
  }
}

function setValueFromDotNotation(obj: any, path: string, value: any) {
  const [key, rest] = path.split(/\.(.+)/);
  if (!rest || rest.length === 0) {
    obj[key] = value;
  } else {
    if (!obj[key]) {
      obj[key] = {};
    }
    setValueFromDotNotation(obj[key], rest, value);
  }
}

function getValueFromDotNotation(obj: any, path: string) {
  const [key, rest] = path.split(/\.(.+)/);
  if (!obj || !obj[key]) {
    return undefined;
  }
  if (!rest || rest.length === 0) {
    return obj[key];
  } else {
    return getValueFromDotNotation(obj[key], rest);
  }
}

